/**
 * Work CMS 캔버스: Work 기준으로 섹션을 렌더하고,
 * data-field-* 기반 인라인 편집/업로드 저장을 지원한다.
 */
import Sortable from "sortablejs";

import { FileUploaderComponent, uploadSingleAttachmentFile, type FileUploaderProps } from "../components/FileUploader";
import { stripBase64Images } from "../../components/QuillEditor";
import { initBlocksCarousel } from "../../home/components/BlocksCarousel";
import { Modal } from "../../utils/Modal";
import { initColorPickerFields } from "../components/colorPickerField";

export class WorkCanvasPreviewPanel {
  readonly #pageRoot: HTMLElement;
  readonly #renderUrl: string;
  readonly #saveTextUrl: string;
  readonly #uploadAttachmentUrl: string;
  readonly #saveAttachmentUrl: string;
  readonly #reorderImageListUrl: string;
  readonly #deleteImageListItemUrl: string;
  readonly #saveImageListMetaUrl: string;
  readonly #saveBlockMetaUrl: string;
  readonly #saveOrderUrl: string;
  readonly #addBlockUrl: string;
  readonly #deleteBlockUrl: string;
  readonly #workId: number;
  #autoSaveEnabled = false;
  readonly #autosaveTimers = new WeakMap<HTMLElement, number>();
  #imageListReloadTimer: number | null = null;

  #body: HTMLElement | null = null;
  #loading: HTMLElement | null = null;
  #error: HTMLElement | null = null;
  #form: HTMLFormElement | null = null;
  #abort: AbortController | null = null;

  constructor(pageRoot: HTMLElement) {
    this.#pageRoot = pageRoot;
    this.#renderUrl = pageRoot.dataset.canvasRenderUrl?.trim() ?? "";
    this.#saveTextUrl = pageRoot.dataset.canvasSaveTextUrl?.trim() ?? "";
    this.#uploadAttachmentUrl = pageRoot.dataset.uploadAttachmentUrl?.trim() ?? "";
    this.#saveAttachmentUrl = pageRoot.dataset.canvasSaveAttachmentUrl?.trim() ?? "";
    this.#reorderImageListUrl = pageRoot.dataset.canvasReorderImageListUrl?.trim() ?? "";
    this.#deleteImageListItemUrl = pageRoot.dataset.canvasDeleteImageListItemUrl?.trim() ?? "";
    this.#saveImageListMetaUrl = pageRoot.dataset.canvasSaveImageListMetaUrl?.trim() ?? "";
    this.#saveBlockMetaUrl = pageRoot.dataset.canvasSaveBlockMetaUrl?.trim() ?? "";
    this.#saveOrderUrl = pageRoot.dataset.canvasSaveOrderUrl?.trim() ?? "";
    this.#addBlockUrl = pageRoot.dataset.canvasAddBlockUrl?.trim() ?? "";
    this.#deleteBlockUrl = pageRoot.dataset.canvasDeleteBlockUrl?.trim() ?? "";
    this.#workId = Number(pageRoot.dataset.workId ?? "0") || 0;
    this.#autoSaveEnabled = true;
  }

  init(): void {
    this.#body = this.#pageRoot.querySelector<HTMLElement>("[data-work-canvas-body]");
    this.#loading = this.#pageRoot.querySelector<HTMLElement>("[data-work-canvas-loading]");
    this.#error = this.#pageRoot.querySelector<HTMLElement>("[data-work-canvas-error]");
    this.#form = this.#pageRoot.querySelector<HTMLFormElement>("[data-work-canvas-form]");

    const refreshBtn = this.#pageRoot.querySelector<HTMLButtonElement>("[data-work-canvas-refresh]");
    refreshBtn?.addEventListener("click", () => void this.load());

    const forceSaveBtn = this.#pageRoot.querySelector<HTMLButtonElement>("[data-work-canvas-force-save]");
    forceSaveBtn?.addEventListener("click", () => void this.#runForceSave(forceSaveBtn));

    const modal = this.#pageRoot.querySelector<HTMLElement>("[data-work-block-picker-modal]");
    const openPicker = this.#pageRoot.querySelector<HTMLButtonElement>("[data-work-canvas-open-block-picker]");
    openPicker?.addEventListener("click", () => this.#openBlockPicker(modal));
    this.#pageRoot.querySelectorAll<HTMLElement>("[data-work-block-picker-close]").forEach((el) => {
      el.addEventListener("click", () => this.#closeBlockPicker(modal));
    });
    const filterInput = this.#pageRoot.querySelector<HTMLInputElement>("[data-work-block-picker-filter]");
    filterInput?.addEventListener("input", () => this.#filterBlockPickerRows(filterInput.value));
    const clearBtn = this.#pageRoot.querySelector<HTMLButtonElement>("[data-admin-search-clear]");
    if (filterInput && clearBtn) {
      const sync = () => {
        clearBtn.hidden = (filterInput.value ?? "").trim().length === 0;
      };
      sync();
      filterInput.addEventListener("input", sync);
      clearBtn.addEventListener("click", () => {
        filterInput.value = "";
        sync();
        this.#filterBlockPickerRows("");
        filterInput.focus();
      });
    }

    this.#pageRoot.querySelectorAll<HTMLButtonElement>("[data-work-block-picker-item]").forEach((btn) => {
      btn.addEventListener("click", () => {
        const slug = btn.dataset.sectionSlug?.trim() ?? "";
        if (!slug) return;
        this.#closeBlockPicker(modal);
        void this.#addBlock(slug);
      });
    });

    document.addEventListener("keydown", (e) => {
      if (e.key !== "Escape") return;
      if (!modal || modal.hidden) return;
      this.#closeBlockPicker(modal);
    });

    void this.load();
  }

  async #runForceSave(btn: HTMLButtonElement): Promise<void> {
    if (btn.disabled) return;
    const prevHtml = btn.innerHTML;
    btn.disabled = true;
    btn.classList.add("is-loading");
    btn.innerHTML = '<i class="fa-solid fa-spinner fa-spin" aria-hidden="true"></i> 저장 중…';
    try {
      await this.#forceSaveAllInlineText();
    } finally {
      btn.innerHTML = prevHtml;
      btn.classList.remove("is-loading");
      btn.disabled = false;
    }
  }

  #openBlockPicker(modal: HTMLElement | null): void {
    if (!modal) return;
    modal.hidden = false;
    modal.setAttribute("aria-hidden", "false");
    const input = modal.querySelector<HTMLInputElement>("[data-work-block-picker-filter]");
    input?.focus();
    input?.select();
  }

  #closeBlockPicker(modal: HTMLElement | null): void {
    if (!modal) return;
    modal.hidden = true;
    modal.setAttribute("aria-hidden", "true");
    const input = modal.querySelector<HTMLInputElement>("[data-work-block-picker-filter]");
    if (input) input.value = "";
    this.#filterBlockPickerRows("");
  }

  #filterBlockPickerRows(raw: string): void {
    const q = raw.trim().toLowerCase();
    this.#pageRoot.querySelectorAll<HTMLElement>("[data-work-block-picker-row]").forEach((row) => {
      const btn = row.querySelector<HTMLButtonElement>("[data-work-block-picker-item]");
      const hay = (btn?.dataset.searchText ?? "").toLowerCase();
      const show = !q || hay.includes(q);
      row.hidden = !show;
    });
  }

  async load(): Promise<void> {
    if (!this.#body || !this.#form || !this.#error) return;
    if (!this.#renderUrl || this.#workId <= 0) return;

    const token = this.#form.querySelector<HTMLInputElement>('input[name="_token"]')?.value ?? "";
    if (!token) return;

    this.#abort?.abort();
    this.#abort = new AbortController();

    this.#error.hidden = true;
    this.#error.textContent = "";
    if (this.#loading) {
      this.#loading.hidden = false;
      this.#loading.style.display = "flex";
    }

    const fd = new FormData();
    fd.append("_token", token);
    fd.append("work_id", String(this.#workId));

    try {
      const res = await fetch(this.#renderUrl, {
        method: "POST",
        body: fd,
        signal: this.#abort.signal,
        headers: {
          Accept: "application/json",
          "X-Requested-With": "XMLHttpRequest",
        },
      });
      if (!res.ok) throw new Error("HTTP " + res.status);
      const data = (await res.json()) as { result?: string; html?: string; message?: string };
      if (data.result !== "success" || typeof data.html !== "string") throw new Error(data.message || "Invalid response");
      this.#body.innerHTML = data.html;

      this.#bindBlockSorter(this.#body, token);
      this.#bindBlockMoveButtons(this.#body, token);
      this.#bindBlockDeleteButtons(this.#body, token);
      this.#bindBlockFitToggles(this.#body, token);
      this.#bindInlineTextEditors(this.#body, token);
      this.#bindInlineMediaUploaders(this.#body, token);
      this.#bindImageListManagers(this.#body, token);
      initColorPickerFields(this.#body, {
        workId: this.#workId,
        saveUrl: this.#saveTextUrl,
        csrfToken: token,
      });
      // CMS 캔버스는 AJAX로 조각 HTML을 갱신하므로, Swiper 등 공개 섹션 초기화는 매번 다시 수행해야 한다.
      initBlocksCarousel();
    } catch (e) {
      if ((e as Error).name === "AbortError") return;
      this.#error.textContent = "CMS 캔버스를 불러오지 못했습니다.";
      this.#error.hidden = false;
    } finally {
      if (this.#loading) {
        this.#loading.hidden = true;
        this.#loading.style.display = "none";
      }
    }
  }

  /**
   * 캔버스 저장 POST 공통 헬퍼.
   * HTTP 오류이거나 서버가 `result: 'fail'` 을 반환하면 에러 패널 + Toast 로 노출하고 throw 한다.
   * (호출부는 보통 `finally { this.load() }` 로 서버 상태에 다시 동기화한다.)
   */
  async #postSave(url: string, fd: FormData): Promise<void> {
    if (!url) {
      this.#showSaveError("저장 주소가 설정되지 않았습니다.");
      throw new Error("missing save url");
    }
    let res: Response;
    try {
      res = await fetch(url, {
        method: "POST",
        body: fd,
        headers: { Accept: "application/json", "X-Requested-With": "XMLHttpRequest" },
      });
    } catch {
      this.#showSaveError("네트워크 오류로 저장하지 못했습니다. 다시 시도해 주세요.");
      throw new Error("network error");
    }

    let msg = "";
    if (res.ok) {
      try {
        const data = (await res.json()) as { result?: string; msg?: string; message?: string };
        if (data && data.result && data.result !== "success") {
          msg = (data.msg ?? data.message ?? "").trim() || "저장에 실패했습니다.";
        }
      } catch {
        /* 비-JSON 200 응답은 성공으로 간주 */
      }
    } else {
      msg = `저장에 실패했습니다. (HTTP ${res.status})`;
    }

    if (msg) {
      this.#showSaveError(msg);
      throw new Error(msg);
    }
  }

  #showSaveError(msg: string): void {
    if (this.#error) {
      this.#error.textContent = msg;
      this.#error.hidden = false;
    }
    (window as unknown as { Toast?: { error?: (m: string) => void } }).Toast?.error?.(msg);
  }

  #bindBlockSorter(root: HTMLElement, token: string): void {
    if (!this.#saveOrderUrl) return;
    const container = root.querySelector<HTMLElement>(".admin-work-templates-cms-preview");
    if (!container) return;
    if (container.dataset.cmsSortableBound === "1") return;
    container.dataset.cmsSortableBound = "1";

    Sortable.create(container, {
      animation: 150,
      draggable: "[data-cms-block]",
      handle: "[data-cms-block-drag]",
      ghostClass: "admin-sortable__ghost",
      dragClass: "admin-sortable__drag",
      onEnd: () => {
        this.#syncMoveButtonState(container);
        void this.#saveOrder(container, token);
      },
    });

    this.#syncMoveButtonState(container);
  }

  #bindBlockMoveButtons(root: HTMLElement, token: string): void {
    if (!this.#saveOrderUrl) return;
    const container = root.querySelector<HTMLElement>(".admin-work-templates-cms-preview");
    if (!container) return;
    if (container.dataset.cmsMoveBound === "1") return;
    container.dataset.cmsMoveBound = "1";

    container.addEventListener("click", (e) => {
      const target = (e.target as HTMLElement | null)?.closest<HTMLElement>("[data-cms-block-move]");
      if (!target) return;
      e.preventDefault();
      e.stopPropagation();

      const dir = (target.dataset.cmsBlockMove ?? "").trim();
      if (dir !== "up" && dir !== "down") return;

      const blk = target.closest<HTMLElement>("[data-cms-block]");
      if (!blk) return;

      const sib = dir === "up" ? (blk.previousElementSibling as HTMLElement | null) : (blk.nextElementSibling as HTMLElement | null);
      if (!sib) return;
      if (!sib.matches("[data-cms-block]")) return;

      if (dir === "up") {
        container.insertBefore(blk, sib);
      } else {
        container.insertBefore(sib, blk);
      }

      this.#syncMoveButtonState(container);
      void this.#saveOrder(container, token);
    });

    this.#syncMoveButtonState(container);
  }

  #syncMoveButtonState(container: HTMLElement): void {
    const blocks = Array.from(container.querySelectorAll<HTMLElement>("[data-cms-block]"));
    blocks.forEach((blk, idx) => {
      const up = blk.querySelector<HTMLButtonElement>('[data-cms-block-move="up"]');
      const down = blk.querySelector<HTMLButtonElement>('[data-cms-block-move="down"]');
      if (up) {
        up.disabled = idx === 0;
        up.setAttribute("aria-disabled", up.disabled ? "true" : "false");
      }
      if (down) {
        down.disabled = idx === blocks.length - 1;
        down.setAttribute("aria-disabled", down.disabled ? "true" : "false");
      }
    });
  }

  async #saveOrder(container: HTMLElement, token: string): Promise<void> {
    const ids: number[] = [];
    container.querySelectorAll<HTMLElement>("[data-cms-block]").forEach((el) => {
      const id = Number(el.dataset.blockId ?? "0") || 0;
      if (id > 0) ids.push(id);
    });
    if (!ids.length) return;

    const fd = new FormData();
    fd.append("_token", token);
    fd.append("work_id", String(this.#workId));
    ids.forEach((id) => fd.append("block_ids[]", String(id)));

    try {
      await this.#postSave(this.#saveOrderUrl, fd);
    } catch {
      /* #postSave 가 이미 노출 */
    }
  }

  /** 블록 헤더의 "이미지 채움 / 이미지 맞춤(여백)" 라디오 → meta_json.media_fit 저장 + 미리보기 즉시 반영. */
  #bindBlockFitToggles(root: HTMLElement, token: string): void {
    if (!this.#saveBlockMetaUrl) return;
    const container = root.querySelector<HTMLElement>(".admin-work-templates-cms-preview");
    if (!container) return;
    if (container.dataset.cmsFitBound === "1") return;
    container.dataset.cmsFitBound = "1";

    container.addEventListener("change", (e) => {
      const input = e.target;
      if (!(input instanceof HTMLInputElement) || input.type !== "radio") return;
      const wrap = input.closest<HTMLElement>("[data-cms-block-fit]");
      if (!wrap) return;
      const blk = input.closest<HTMLElement>("[data-cms-block]");
      const blockId = Number(wrap.dataset.blockId ?? blk?.dataset.blockId ?? "0") || 0;
      if (blockId <= 0) return;
      const fit = (input.value ?? "").trim() === "contain" ? "contain" : "cover";

      const scope = blk?.querySelector<HTMLElement>("[data-cms-fit-scope]");
      if (scope) {
        scope.classList.toggle("no-media-fit", fit === "contain");
        scope.classList.toggle("no-media-fit--contain", fit === "contain");
      }

      const fd = new FormData();
      fd.append("_token", token);
      fd.append("work_id", String(this.#workId));
      fd.append("block_id", String(blockId));
      fd.append("media_fit", fit);
      this.#postSave(this.#saveBlockMetaUrl, fd).catch(() => {
        /* #postSave 가 이미 에러를 노출함 — 다음 새로고침 시 서버 상태로 재동기화 */
      });
    });
  }

  #bindBlockDeleteButtons(root: HTMLElement, token: string): void {
    if (!this.#deleteBlockUrl) return;
    root.querySelectorAll<HTMLElement>("[data-cms-block]").forEach((blk) => {
      if (blk.querySelector("[data-cms-block-delete]")) return;
      const id = Number(blk.dataset.blockId ?? "0") || 0;
      if (id <= 0) return;
      const head = blk.querySelector<HTMLElement>(".admin-cms-block__head");
      if (!head) return;

      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = "admin-btn admin-btn--secondary admin-btn--sm";
      btn.textContent = "삭제";
      btn.setAttribute("data-cms-block-delete", "1");
      btn.addEventListener("click", (e) => {
        e.preventDefault();
        e.stopPropagation();
        void Modal.confirm("이 섹션을 삭제할까요? 입력된 값은 더 이상 렌더되지 않습니다.").then((ok) => {
          if (!ok) return;
          void this.#deleteBlock(id, token);
        });
      });
      head.appendChild(btn);
    });
  }

  async #addBlock(sectionSlug: string): Promise<void> {
    if (!this.#addBlockUrl || !this.#form) return;
    const token = this.#form.querySelector<HTMLInputElement>('input[name="_token"]')?.value ?? "";
    if (!token) return;
    const fd = new FormData();
    fd.append("_token", token);
    fd.append("work_id", String(this.#workId));
    fd.append("section_slug", sectionSlug);
    try {
      await this.#postSave(this.#addBlockUrl, fd);
    } catch {
      /* #postSave 가 이미 노출 */
    } finally {
      void this.load();
    }
  }

  async #deleteBlock(blockId: number, token: string): Promise<void> {
    const fd = new FormData();
    fd.append("_token", token);
    fd.append("work_id", String(this.#workId));
    fd.append("block_id", String(blockId));
    try {
      await this.#postSave(this.#deleteBlockUrl, fd);
    } catch {
      /* #postSave 가 이미 노출 */
    } finally {
      void this.load();
    }
  }

  /** 텍스트/리치 비교용(공백·NBSP·빈 에디터 잔여 마크업 정규화). */
  #fingerprintInlineField(el: HTMLElement, type: "text" | "richtext"): string {
    if (type === "text") {
      return (el.textContent ?? "").replace(/\u00a0/g, " ").trim();
    }
    const probe = document.createElement("div");
    probe.innerHTML = el.innerHTML.trim();
    const plain = (probe.innerText ?? "").replace(/\u00a0/g, " ").trim();
    if (!plain) return "";
    return el.innerHTML.trim().replace(/\u00a0/g, " ");
  }

  #syncCmsEmptyHint(el: HTMLElement, type: "text" | "richtext"): void {
    const empty = this.#fingerprintInlineField(el, type) === "";
    if (empty) el.setAttribute("data-cms-empty", "1");
    else el.removeAttribute("data-cms-empty");
  }

  #serializeInlineForSave(el: HTMLElement, type: "text" | "richtext"): string {
    if (type === "text") {
      return this.#fingerprintInlineField(el, "text");
    }
    if (this.#fingerprintInlineField(el, "richtext") === "") return "";
    // Prevent accidental base64 data URLs being stored in DB.
    return stripBase64Images(el.innerHTML.trim());
  }

  #bindInlineTextEditors(root: HTMLElement, token: string): void {
    if (!this.#saveTextUrl) return;
    root.querySelectorAll<HTMLElement>(
      "[data-field-key][data-field-type='text'],[data-field-key][data-field-type='richtext']",
    ).forEach((el) => {
      if (el.dataset.cmsBound === "1") return;
      el.dataset.cmsBound = "1";
      el.tabIndex = 0;
      const ft = (el.dataset.fieldType ?? "").trim();
      if (ft === "text" || ft === "richtext") {
        const t = ft as "text" | "richtext";
        el.dataset.cmsBaseline = this.#fingerprintInlineField(el, t);
        this.#syncCmsEmptyHint(el, t);
      }
      el.addEventListener("focusin", () => {
        el.classList.add("is-cms-editing");
      });
      el.addEventListener("focusout", () => {
        el.classList.remove("is-cms-editing");
      });
      // 첫 클릭/첫 키 입력부터 바로 편집 가능하도록(더블클릭 방지)
      el.addEventListener("pointerdown", (e) => {
        if ((e as PointerEvent).button !== 0) return;
        if (el.isContentEditable) return;
        this.#enterEditMode(el);
      });
      el.addEventListener("keydown", (e) => {
        const isTextField = (el.dataset.fieldType ?? "").trim() === "text";
        // text 필드(제목 등)는 한 줄 — Enter 로 줄바꿈 대신 편집 확정(blur).
        if (isTextField && e.key === "Enter" && (el.isContentEditable || document.activeElement === el)) {
          e.preventDefault();
          el.blur();
          return;
        }
        if (el.isContentEditable || document.activeElement === el) return;
        // printable key → 바로 편집 모드 진입
        if (e.key.length === 1 || e.key === "Backspace" || e.key === "Enter") {
          this.#enterEditMode(el);
        }
      });
      el.addEventListener("blur", () => {
        if (el.isContentEditable) el.contentEditable = "false";
        // autosave 토글과 무관하게 blur 저장 시 리렌더로 UX가 흔들리지 않도록 reload는 하지 않는다.
        void this.#flushInlineFieldIfDirty(el, token, { reload: false });
      });
      el.addEventListener("input", () => {
        const t = (el.dataset.fieldType ?? "").trim();
        if (t === "text" || t === "richtext") this.#syncCmsEmptyHint(el, t as "text" | "richtext");
        if (!this.#autoSaveEnabled) return;
        this.#debouncedAutosave(el, token);
      });
    });
  }

  /**
   * 인라인 필드를 편집 모드로 전환.
   * text 필드는 `plaintext-only` 를 우선 시도하되, 미지원 브라우저(구 Firefox 등)에서는
   * `el.isContentEditable` 가 false 로 남아 입력 자체가 안 되므로 일반 `true` 로 폴백한다.
   * (저장 시 #serializeInlineForSave('text') 가 textContent 만 취하므로 결과는 어차피 평문)
   */
  #enterEditMode(el: HTMLElement): void {
    const isText = (el.dataset.fieldType ?? "").trim() === "text";
    el.contentEditable = isText ? "plaintext-only" : "true";
    if (!el.isContentEditable) el.contentEditable = "true";
    requestAnimationFrame(() => el.focus());
  }

  #debouncedAutosave(el: HTMLElement, token: string): void {
    const prev = this.#autosaveTimers.get(el);
    if (prev) window.clearTimeout(prev);
    const tid = window.setTimeout(() => {
      void this.#flushInlineFieldIfDirty(el, token, { reload: false });
    }, 650);
    this.#autosaveTimers.set(el, tid);
  }

  // autosave is always enabled (debounced) for text fields

  /**
   * 값이 초기 로드 시점과 다를 때만 저장. blur 기본 동작.
   */
  async #flushInlineFieldIfDirty(
    el: HTMLElement,
    token: string,
    opts?: { force?: boolean; reload?: boolean },
  ): Promise<void> {
    const type = (el.dataset.fieldType ?? "").trim();
    if (type !== "text" && type !== "richtext") return;
    const t = type as "text" | "richtext";
    const fp = this.#fingerprintInlineField(el, t);
    const baseline = el.dataset.cmsBaseline ?? "";
    if (!opts?.force && fp === baseline) {
      this.#syncCmsEmptyHint(el, t);
      return;
    }
    const reload = opts?.reload !== false;
    await this.#saveInlineText(el, token, { reload });
    if (!reload) {
      el.dataset.cmsBaseline = this.#fingerprintInlineField(el, t);
      this.#syncCmsEmptyHint(el, t);
    }
  }

  async #forceSaveAllInlineText(): Promise<void> {
    if (!this.#body || !this.#form || !this.#saveTextUrl) return;
    const token = this.#form.querySelector<HTMLInputElement>('input[name="_token"]')?.value ?? "";
    if (!token) return;

    const fields = Array.from(
      this.#body.querySelectorAll<HTMLElement>(
        "[data-field-key][data-field-type='text'],[data-field-key][data-field-type='richtext']",
      ),
    );
    for (const el of fields) {
      await this.#flushInlineFieldIfDirty(el, token, { force: true, reload: false });
    }
    void this.load();
  }

  async #saveInlineText(el: HTMLElement, token: string, options?: { reload?: boolean }): Promise<void> {
    const reload = options?.reload !== false;
    const blockId = Number(el.dataset.fieldBlockId ?? "0") || 0;
    const fieldKey = (el.dataset.fieldKey ?? "").trim();
    const type = (el.dataset.fieldType ?? "").trim();
    if (blockId <= 0 || !fieldKey) return;

    const valueType = type === "richtext" ? "richtext" : "text";
    const valueText =
      valueType === "richtext"
        ? this.#serializeInlineForSave(el, "richtext")
        : this.#serializeInlineForSave(el, "text");

    const fd = new FormData();
    fd.append("_token", token);
    fd.append("work_id", String(this.#workId));
    fd.append("block_id", String(blockId));
    fd.append("field_key", fieldKey);
    fd.append("value_type", valueType);
    fd.append("value_text", valueText);

    try {
      await this.#postSave(this.#saveTextUrl, fd);
    } catch {
      /* #postSave 가 이미 노출 */
    } finally {
      if (reload) void this.load();
    }
  }

  #bindInlineMediaUploaders(root: HTMLElement, token: string): void {
    if (!this.#uploadAttachmentUrl || !this.#saveAttachmentUrl) return;

    root.querySelectorAll<HTMLElement>(
      "[data-field-key][data-field-type='image'],[data-field-key][data-field-type='video']",
    ).forEach((el) => {
      if (el.dataset.cmsMediaBound === "1") return;

      if (el.classList.contains("admin-cms-media-placeholder") || el.classList.contains("admin-cms-media-slot")) {
        el.dataset.cmsMediaBound = "1";
        this.#initCmsSlotFileUploader(el, token);
        return;
      }

      el.dataset.cmsMediaBound = "1";
      el.addEventListener("click", (e) => {
        e.preventDefault();
        e.stopPropagation();
        const multiple = (el.dataset.fieldType ?? "") === "image_list";
        void this.#pickAndUpload(el, token, multiple);
      });
      this.#bindDropzone(el, token);
    });
  }

  #initCmsImageListUploader(uploaderRoot: HTMLElement, token: string): void {
    if (uploaderRoot.dataset.cmsListUploaderBound === "1") return;
    uploaderRoot.dataset.cmsListUploaderBound = "1";
    uploaderRoot.classList.add("admin-file-uploader-box", "admin-file-uploader-box--cms-slot");

    const blockId = Number(uploaderRoot.dataset.fieldBlockId ?? "0") || 0;
    const fieldKey = (uploaderRoot.dataset.fieldKey ?? "").trim();
    if (blockId <= 0 || !fieldKey) return;

    const maxCountRaw = Number(uploaderRoot.dataset.fieldMaxCount ?? "0") || 0;
    const props: FileUploaderProps = {
      uploadUrl: this.#uploadAttachmentUrl,
      category: "image",
      targetType: "temp",
      targetId: 0,
      multiple: true,
      uiVariant: "cms-slot",
      accept: "image/*",
      maxCount: maxCountRaw > 0 ? maxCountRaw : undefined,
      onUploadComplete: async (item) => {
        if (item.id <= 0) return;
        await this.#saveAttachmentLink(token, {
          blockId,
          fieldKey,
          valueType: "image_list",
          attachmentId: item.id,
        });
        // 여러 파일 업로드 시에도 "무반응"처럼 보이지 않도록 load() 를 디바운스한다.
        this.#scheduleImageListReload();
      },
    };

    const comp = new FileUploaderComponent(uploaderRoot, props);
    comp.init();
    uploaderRoot._fileUploaderComponent = comp;
  }

  #scheduleImageListReload(): void {
    if (this.#imageListReloadTimer != null) window.clearTimeout(this.#imageListReloadTimer);
    this.#imageListReloadTimer = window.setTimeout(() => {
      this.#imageListReloadTimer = null;
      void this.load();
    }, 250);
  }

  /** 공용 FileUploader 의 cms-slot 모드 — 클릭/드롭·검증·로더 동일 */
  #initCmsSlotFileUploader(el: HTMLElement, token: string): void {
    const rawType = (el.dataset.fieldType ?? "").trim();
    if (rawType !== "image" && rawType !== "video") return;

    el.classList.add("admin-file-uploader-box", "admin-file-uploader-box--cms-slot");

    const category = rawType === "video" ? "video" : "image";
    const props: FileUploaderProps = {
      uploadUrl: this.#uploadAttachmentUrl,
      category,
      targetType: "temp",
      targetId: 0,
      multiple: false,
      uiVariant: "cms-slot",
      accept: category === "video" ? "video/*" : "image/*",
      onUploadComplete: async (item) => {
        const blockId = Number(el.dataset.fieldBlockId ?? "0") || 0;
        const fieldKey = (el.dataset.fieldKey ?? "").trim();
        if (blockId <= 0 || !fieldKey || item.id <= 0) return;
        await this.#saveAttachmentLink(token, {
          blockId,
          fieldKey,
          valueType: rawType,
          attachmentId: item.id,
        });
        void this.load();
      },
    };

    const comp = new FileUploaderComponent(el, props);
    comp.init();
    // cms-slot은 기존 미디어 위에 오버레이로 노출 (교체 가능)
    const dz = el.querySelector<HTMLElement>(".admin-file-uploader__dropzone");
    if (dz) dz.classList.add("is-cms-slot-overlay");
    el._fileUploaderComponent = comp;

    // 단일 슬롯(image/video) 삭제 UX 제공 (기존 row 삭제)
    this.#ensureSingleMediaDeleteButton(el, token);
  }

  #ensureSingleMediaDeleteButton(el: HTMLElement, token: string): void {
    if (!this.#saveAttachmentUrl) return;
    if (el.querySelector("[data-cms-single-media-delete]")) return;
    const blockId = Number(el.dataset.fieldBlockId ?? "0") || 0;
    const fieldKey = (el.dataset.fieldKey ?? "").trim();
    const valueType = (el.dataset.fieldType ?? "").trim();
    if (blockId <= 0 || !fieldKey) return;
    if (valueType !== "image" && valueType !== "video") return;

    const hasValue = (el.dataset.cmsValue ?? "").trim().length > 0 || Boolean(el.querySelector("img,video"));
    if (!hasValue) return;

    el.style.position = el.style.position || "relative";
    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = "admin-cms-media__delete";
    btn.textContent = "×";
    btn.setAttribute("data-cms-single-media-delete", "1");
    btn.addEventListener("click", (e) => {
      e.preventDefault();
      e.stopPropagation();
      void Modal.confirm("이미지를 삭제할까요?").then((ok) => {
        if (!ok) return;
        void this.#clearSingleMediaField(token, { blockId, fieldKey, valueType });
      });
    });
    el.appendChild(btn);
  }

  async #clearSingleMediaField(
    token: string,
    args: { blockId: number; fieldKey: string; valueType: "image" | "video" },
  ): Promise<void> {
    const fd = new FormData();
    fd.append("_token", token);
    fd.append("work_id", String(this.#workId));
    fd.append("block_id", String(args.blockId));
    fd.append("field_key", args.fieldKey);
    fd.append("value_type", args.valueType);
    // server-side: attachment_id=0 => delete row
    fd.append("attachment_id", "0");
    try {
      await this.#postSave(this.#saveAttachmentUrl, fd);
    } catch {
      /* #postSave 가 이미 노출 */
    } finally {
      void this.load();
    }
  }

  async #pickAndUpload(el: HTMLElement, token: string, multiple: boolean): Promise<void> {
    const type = (el.dataset.fieldType ?? "").trim();
    const blockId = Number(el.dataset.fieldBlockId ?? "0") || 0;
    const fieldKey = (el.dataset.fieldKey ?? "").trim();
    if (!type || blockId <= 0 || !fieldKey) return;

    const input = document.createElement("input");
    input.type = "file";
    input.multiple = multiple;
    input.accept = type === "video" ? ".mp4,.webm,.ogg,.mov" : ".png,.jpg,.jpeg,.webp,.gif";
    input.click();

    await new Promise<void>((resolve) => input.addEventListener("change", () => resolve(), { once: true }));
    const files = input.files ? Array.from(input.files) : [];
    if (!files.length) return;

    const queue = multiple ? files : files.slice(0, 1);
    for (const file of queue) {
      const attachmentId = await this.#uploadAttachment(file, token, type === "video" ? "video" : "image");
      if (!attachmentId) continue;
      await this.#saveAttachmentLink(token, { blockId, fieldKey, valueType: type, attachmentId });
    }

    void this.load();
  }

  async #uploadAttachment(file: File, _token: string, category: "image" | "video"): Promise<number> {
    const item = await uploadSingleAttachmentFile(file, {
      uploadUrl: this.#uploadAttachmentUrl,
      category,
      targetType: "temp",
      targetId: 0,
    });
    return item?.id ?? 0;
  }

  async #saveAttachmentLink(
    token: string,
    args: { blockId: number; fieldKey: string; valueType: string; attachmentId: number },
  ): Promise<void> {
    const fd = new FormData();
    fd.append("_token", token);
    fd.append("work_id", String(this.#workId));
    fd.append("block_id", String(args.blockId));
    fd.append("field_key", args.fieldKey);
    fd.append("value_type", args.valueType);
    fd.append("attachment_id", String(args.attachmentId));

    try {
      await this.#postSave(this.#saveAttachmentUrl, fd);
    } catch {
      /* #postSave 가 이미 노출 */
    }
  }

  #bindImageListManagers(root: HTMLElement, token: string): void {
    if (!this.#reorderImageListUrl || !this.#deleteImageListItemUrl || !this.#saveImageListMetaUrl) return;

    root.querySelectorAll<HTMLElement>("[data-field-key][data-field-type='image_list']").forEach((listEl) => {
      if (listEl.dataset.cmsListBound === "1") return;
      listEl.dataset.cmsListBound = "1";

      // FileUploader UI 통일: 리스트 위에 공통 드롭존을 항상 노출
      const parent = listEl.parentElement;
      if (parent) {
        let up = parent.querySelector<HTMLElement>("[data-cms-image-list-uploader]");
        if (!up) {
          up = document.createElement("div");
          up.setAttribute("data-cms-image-list-uploader", "1");
          up.className = "admin-cms-media-placeholder admin-cms-media-placeholder--list-uploader";
          up.dataset.fieldKey = listEl.dataset.fieldKey ?? "";
          up.dataset.fieldType = "image_list";
          up.dataset.fieldBlockId = listEl.dataset.fieldBlockId ?? "";
          // 선택: PHP에서 내려준 maxCount 사용
          if (listEl.dataset.fieldMaxCount) up.dataset.fieldMaxCount = listEl.dataset.fieldMaxCount;
          parent.insertBefore(up, listEl);
        }
        this.#initCmsImageListUploader(up, token);
      }

      // image_list 가 <ul><li> 형태가 아닐 수 있음 (예: Swiper carousel).
      const isSwiperWrapper =
        listEl.classList.contains("swiper-wrapper") ||
        Boolean(listEl.closest(".swiper")) ||
        Boolean(listEl.querySelector(".swiper-slide"));

      if (isSwiperWrapper) {
        // Swiper 슬라이드 기반 정렬/삭제/다음 슬롯 표시
        Sortable.create(listEl, {
          animation: 150,
          draggable: ".swiper-slide",
          onEnd: () => void this.#saveImageListOrderFromCarousel(listEl, token),
        });
        this.#bindCarouselImageListDeleteButtons(listEl, token);
        this.#markNextEmptyCarouselSlot(listEl);
      } else {
        Sortable.create(listEl, {
          animation: 150,
          draggable: "li",
          onEnd: () => void this.#saveImageListOrder(listEl, token),
        });
        listEl.querySelectorAll<HTMLImageElement>("img[data-field-attachment-id]").forEach((img) => {
          const attId = Number(img.dataset.fieldAttachmentId ?? "0") || 0;
          if (attId <= 0) return;
          const li = img.closest("li") as HTMLElement | null;
          if (!li) return;
          if (li.querySelector("[data-cms-delete-item]")) return;

          li.style.position = li.style.position || "relative";
          const btn = document.createElement("button");
          btn.type = "button";
          btn.textContent = "×";
          btn.className = "admin-cms-media__delete";
          btn.setAttribute("data-cms-delete-item", "1");
          btn.addEventListener("click", (e) => {
            e.preventDefault();
            e.stopPropagation();
            void this.#deleteImageListItem(listEl, attId, token);
          });
          li.appendChild(btn);
        });
      }

      listEl.querySelectorAll<HTMLElement>("[data-field-caption][data-field-attachment-id]").forEach((cap) => {
        if (cap.dataset.cmsCaptionBound === "1") return;
        cap.dataset.cmsCaptionBound = "1";
        const attId = Number(cap.dataset.fieldAttachmentId ?? "0") || 0;
        if (attId <= 0) return;
        cap.tabIndex = 0;
        cap.dataset.cmsCaptionBaseline = (cap.textContent ?? "").replace(/\u00a0/g, " ").trim();
        cap.addEventListener("focusin", () => cap.classList.add("is-cms-editing"));
        cap.addEventListener("focusout", () => cap.classList.remove("is-cms-editing"));
        cap.addEventListener("click", (e) => {
          e.preventDefault();
          e.stopPropagation();
          cap.contentEditable = "true";
          cap.focus();
        });
        cap.addEventListener("blur", () => {
          cap.contentEditable = "false";
          const next = (cap.textContent ?? "").replace(/\u00a0/g, " ").trim();
          const base = (cap.dataset.cmsCaptionBaseline ?? "").replace(/\u00a0/g, " ").trim();
          if (next === base) return;
          cap.dataset.cmsCaptionBaseline = next;
          void this.#saveImageListMeta(listEl, attId, { caption: cap.textContent ?? "" }, token);
        });
      });
    });
  }

  #bindCarouselImageListDeleteButtons(listEl: HTMLElement, token: string): void {
    listEl.querySelectorAll<HTMLElement>(".swiper-slide").forEach((slide) => {
      if (slide.querySelector("[data-cms-delete-item]")) return;
      const media = slide.querySelector<HTMLElement>("[data-media-slot]") ?? slide;
      const img = slide.querySelector<HTMLElement>("[data-field-attachment-id]") as HTMLElement | null;
      const attId = Number(img?.getAttribute("data-field-attachment-id") ?? "0") || 0;
      if (attId <= 0) return;

      (media as HTMLElement).style.position = (media as HTMLElement).style.position || "relative";
      const btn = document.createElement("button");
      btn.type = "button";
      btn.textContent = "×";
      btn.className = "admin-cms-media__delete";
      btn.setAttribute("data-cms-delete-item", "1");
      btn.addEventListener("click", (e) => {
        e.preventDefault();
        e.stopPropagation();
        void this.#deleteImageListItem(listEl, attId, token);
      });
      media.appendChild(btn);
    });
  }

  #markNextEmptyCarouselSlot(listEl: HTMLElement): void {
    // "다음 업로드될 슬롯"이 어디인지 시각적으로 표시
    listEl.querySelectorAll<HTMLElement>(".swiper-slide").forEach((s) => s.classList.remove("is-cms-next-slot"));
    const slides = Array.from(listEl.querySelectorAll<HTMLElement>(".swiper-slide"));
    const next = slides.find((slide) => {
      const hasAtt = Boolean(slide.querySelector("[data-field-attachment-id]"));
      return !hasAtt;
    });
    if (next) next.classList.add("is-cms-next-slot");
  }

  async #saveImageListOrderFromCarousel(listEl: HTMLElement, token: string): Promise<void> {
    const blockId = Number(listEl.dataset.fieldBlockId ?? "0") || 0;
    const fieldKey = (listEl.dataset.fieldKey ?? "").trim();
    if (blockId <= 0 || !fieldKey) return;

    const ids: number[] = [];
    listEl.querySelectorAll<HTMLElement>(".swiper-slide").forEach((slide) => {
      const el = slide.querySelector<HTMLElement>("[data-field-attachment-id]");
      const id = Number(el?.getAttribute("data-field-attachment-id") ?? "0") || 0;
      if (id > 0) ids.push(id);
    });
    if (!ids.length) return;

    const fd = new FormData();
    fd.append("_token", token);
    fd.append("work_id", String(this.#workId));
    fd.append("block_id", String(blockId));
    fd.append("field_key", fieldKey);
    ids.forEach((id) => fd.append("attachment_ids[]", String(id)));

    try {
      await this.#postSave(this.#reorderImageListUrl, fd);
    } catch {
      /* #postSave 가 이미 노출 */
    }
  }

  async #saveImageListOrder(listEl: HTMLElement, token: string): Promise<void> {
    const blockId = Number(listEl.dataset.fieldBlockId ?? "0") || 0;
    const fieldKey = (listEl.dataset.fieldKey ?? "").trim();
    if (blockId <= 0 || !fieldKey) return;

    const ids: number[] = [];
    listEl.querySelectorAll<HTMLImageElement>("img[data-field-attachment-id]").forEach((img) => {
      const id = Number(img.dataset.fieldAttachmentId ?? "0") || 0;
      if (id > 0) ids.push(id);
    });
    if (!ids.length) return;

    const fd = new FormData();
    fd.append("_token", token);
    fd.append("work_id", String(this.#workId));
    fd.append("block_id", String(blockId));
    fd.append("field_key", fieldKey);
    ids.forEach((id) => fd.append("attachment_ids[]", String(id)));

    try {
      await this.#postSave(this.#reorderImageListUrl, fd);
    } catch {
      /* #postSave 가 이미 노출 */
    }
  }

  async #deleteImageListItem(listEl: HTMLElement, attachmentId: number, token: string): Promise<void> {
    const blockId = Number(listEl.dataset.fieldBlockId ?? "0") || 0;
    const fieldKey = (listEl.dataset.fieldKey ?? "").trim();
    if (blockId <= 0 || !fieldKey) return;

    const fd = new FormData();
    fd.append("_token", token);
    fd.append("work_id", String(this.#workId));
    fd.append("block_id", String(blockId));
    fd.append("field_key", fieldKey);
    fd.append("attachment_id", String(attachmentId));

    try {
      await this.#postSave(this.#deleteImageListItemUrl, fd);
    } catch {
      /* #postSave 가 이미 노출 */
    }
    void this.load();
  }

  async #saveImageListMeta(
    listEl: HTMLElement,
    attachmentId: number,
    meta: { caption?: string; alt?: string },
    token: string,
  ): Promise<void> {
    const blockId = Number(listEl.dataset.fieldBlockId ?? "0") || 0;
    const fieldKey = (listEl.dataset.fieldKey ?? "").trim();
    if (blockId <= 0 || !fieldKey) return;

    const fd = new FormData();
    fd.append("_token", token);
    fd.append("work_id", String(this.#workId));
    fd.append("block_id", String(blockId));
    fd.append("field_key", fieldKey);
    fd.append("attachment_id", String(attachmentId));
    fd.append("meta_json", JSON.stringify(meta));

    try {
      await this.#postSave(this.#saveImageListMetaUrl, fd);
    } catch {
      /* #postSave 가 이미 노출 */
    }
  }

  #bindDropzone(el: HTMLElement, token: string): void {
    if (el.dataset.fieldDropBound === "1") return;
    el.dataset.fieldDropBound = "1";
    el.addEventListener("dragover", (e) => {
      e.preventDefault();
      if (e.dataTransfer) e.dataTransfer.dropEffect = "copy";
      el.classList.add("is-drop-over");
    });
    el.addEventListener("dragleave", () => {
      el.classList.remove("is-drop-over");
    });
    el.addEventListener("drop", (e) => {
      e.preventDefault();
      el.classList.remove("is-drop-over");
      const files = e.dataTransfer ? Array.from(e.dataTransfer.files ?? []) : [];
      if (!files.length) return;
      const multiple = (el.dataset.fieldType ?? "") === "image_list";
      void this.#uploadDroppedFiles(el, token, files, multiple);
    });
  }

  async #uploadDroppedFiles(el: HTMLElement, token: string, files: File[], multiple: boolean): Promise<void> {
    const type = (el.dataset.fieldType ?? "").trim();
    const blockId = Number(el.dataset.fieldBlockId ?? "0") || 0;
    const fieldKey = (el.dataset.fieldKey ?? "").trim();
    if (!type || blockId <= 0 || !fieldKey) return;
    const queue = multiple ? files : files.slice(0, 1);
    for (const file of queue) {
      const attachmentId = await this.#uploadAttachment(file, token, type === "video" ? "video" : "image");
      if (!attachmentId) continue;
      await this.#saveAttachmentLink(token, { blockId, fieldKey, valueType: type, attachmentId });
    }
    void this.load();
  }
}

